import { Alert, Button, Card, CardContent, Spinner } from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { formatDate, householdRoleLabel } from '@/features/shared/labels';
import { PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

/** Public route: shows the invitation preview to guests, accept/decline needs a login. */
export function InvitationPage() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const preview = useQuery({
    queryKey: queryKeys.households.invitation(token),
    queryFn: () => api.households.previewInvitation(token),
    enabled: Boolean(token),
  });
  const accept = useMutation({
    mutationFn: () => api.households.acceptInvitation({ token }),
    onSuccess: (household) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
      toast.success(t('households.invitation.accepted', { name: household.name }));
      navigate(`/households/${household.id}`, { replace: true });
    },
    onError: (e) => toastError(e),
  });
  const decline = useMutation({
    mutationFn: () => api.households.declineInvitation({ token }),
    onSuccess: () => {
      toast.success(t('households.invitation.declined'));
      navigate('/', { replace: true });
    },
    onError: (e) => toastError(e),
  });

  const loginState = { from: location.pathname };

  return (
    <PageContainer className="max-w-lg">
      <h1 className="mb-4 text-2xl font-semibold">{t('households.invitation.title')}</h1>
      {preview.isLoading && <Spinner label={t('app.loading')} />}
      {preview.error && <Alert variant="destructive">{describeError(preview.error)}</Alert>}
      {preview.data && (
        <Card>
          <CardContent className="space-y-4 p-4 md:p-6">
            <p>
              {t('households.invitation.body', {
                inviter: preview.data.invitedBy.displayName,
                household: preview.data.householdName,
                role: householdRoleLabel(t, preview.data.role),
              })}
            </p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{t('auth.email')}</dt>
              <dd>{preview.data.email}</dd>
              <dt className="text-muted-foreground">{t('households.table.expires')}</dt>
              <dd>{formatDate(preview.data.expiresAt, i18n.language, true)}</dd>
            </dl>
            {preview.data.status !== 'PENDING' && (
              <Alert variant="warning">
                {t(`households.invitation.status.${preview.data.status}`)}
              </Alert>
            )}
            {preview.data.status === 'PENDING' && status === 'anonymous' && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('households.invitation.loginRequired')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild>
                    <Link to="/login" state={loginState}>
                      {t('nav.login')}
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/register" state={loginState}>
                      {t('nav.register')}
                    </Link>
                  </Button>
                </div>
              </div>
            )}
            {preview.data.status === 'PENDING' && status === 'authenticated' && (
              <div className="space-y-2">
                {user && user.email.toLowerCase() !== preview.data.email.toLowerCase() && (
                  <Alert variant="warning">
                    {t('households.invitation.emailMismatch', { email: preview.data.email })}
                  </Alert>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    loading={accept.isPending}
                    onClick={() => accept.mutate()}
                    data-testid="invitation-accept"
                  >
                    {t('households.invitation.accept')}
                  </Button>
                  <Button
                    variant="outline"
                    loading={decline.isPending}
                    onClick={() => decline.mutate()}
                  >
                    {t('households.invitation.decline')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
